import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = 'full' } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    let sectionHTML = '';
    try {
      sectionHTML = await this.getSectionHTML(sectionId, cache, url, abortController.signal);
    } catch (error) {
      // An aborted render resolves empty rather than rejecting: every caller that awaits it
      // wants the fresher morph that cancelled it, not an error. `sectionHTML` is still '' here.
      if (abortController.signal.aborted) return sectionHTML;
      throw error;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
    }

    // Empty means "nothing was morphed", and callers key off that. An abort after the fetch
    // resolved skips the rejection path above, so check it here too.
    if (abortController.signal.aborted) return '';

    morphSection(sectionId, sectionHTML, { mode });

    return sectionHTML;
  }

  /**
   * Cancels a pending render for a section. Call it before morphing fresher HTML into a section
   * that a render may still be fetching, or the stale response lands on top of the morph.
   * @param {string} sectionId - The section ID
   */
  abortRender(sectionId) {
    this.#abortPendingMorph(sectionId);
  }

  /**
   * Aborts an existing render for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
      this.#abortControllersBySectionId.delete(sectionId);
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {AbortSignal} [signal] - Cancels the fetch. A signalled fetch joins an unsignalled one
   * already in flight but is never shared onward, since its abort would take the sharers down.
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), signal) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl, { signal }).then((response) => {
      return response.text();
    });

    if (!signal) this.#pendingPromises.set(sectionUrl, pendingPromise);

    // Cleared even when the fetch rejects. A retained rejected promise is handed to every later
    // caller for this URL, so one failed request would poison the page for good.
    try {
      const sectionHTML = await pendingPromise;

      this.#cache.set(sectionUrl, sectionHTML);
      return sectionHTML;
    } finally {
      if (!signal) this.#pendingPromises.delete(sectionUrl);
    }
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {Object} [options] - Additional options
 * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 * @param {boolean} [options.injectStylesheet=false] - When true, a hydration morph also brings in
 *   the response's `style[data-section-stylesheet]` (see `hoistSectionStylesheet`). A full morph
 *   always does.
 */
export async function morphSection(sectionId, html, options = {}) {
  const { mode = 'full', injectStylesheet = false } = options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  // A full morph would carry the stylesheet into the section; a hydration morph only reaches keyed
  // nodes, which never hold it.
  if (mode === 'full' || injectStylesheet) hoistSectionStylesheet(sectionId, fragment);

  const modalDialogs = modalDialogsIn(existingElement);

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });

  restoreModality(modalDialogs);
}

/** The bundle link, then the custom sheets that follow it; the first one present is the anchor. */
const SECTION_STYLESHEET_ANCHOR =
  'link[rel="stylesheet"][href*="/compiled_assets/styles.css"], ' +
  'link[rel="stylesheet"][href*="/assets/custom."]';

/**
 * Takes a section response's `<style data-section-stylesheet>` out of the response and puts it in
 * `<head>`, just ahead of the page's stylesheet bundle. Morphed in with the markup it would land in
 * `<body>` and beat the `custom.*.css` overrides; `docs/upstream.md` has why. Each section keeps at
 * most one in `<head>`; a later render's style replaces it.
 *
 * @param {string} sectionId - The section ID
 * @param {ParentNode} response - The parsed Section Rendering API response
 */
export function hoistSectionStylesheet(sectionId, response) {
  const stylesheet = response.querySelector('style[data-section-stylesheet]');
  if (!stylesheet) return;

  stylesheet.remove();

  const key = normalizeSectionId(sectionId);
  const hoisted = document.head.querySelector(
    `style[data-section-stylesheet="${CSS.escape(key)}"]`
  );
  if (hoisted?.textContent === stylesheet.textContent) return;

  stylesheet.setAttribute('data-section-stylesheet', key);

  // If the bundle's path ever changes, the first custom sheet keeps the custom sheets last.
  const anchor = document.head.querySelector(SECTION_STYLESHEET_ANCHOR);
  if (hoisted) {
    hoisted.replaceWith(stylesheet);
  } else if (anchor) {
    anchor.before(stylesheet);
  } else {
    document.head.append(stylesheet);
  }
}

/**
 * The open modal dialogs inside a section, read before it is morphed.
 * @param {HTMLElement} section
 * @returns {HTMLDialogElement[]}
 */
function modalDialogsIn(section) {
  return [...section.querySelectorAll('dialog')].filter((dialog) => dialog.matches(':modal'));
}

/**
 * Puts back the top layer a morph dropped.
 *
 * Morph moves a node by removing and re-inserting it, and re-inserting an open modal dialog pops it
 * out of the top layer: the `open` attribute survives, the modality does not. What is left is a
 * drawer with no backdrop over a page whose scroll lock nothing releases, and a trigger that
 * no-ops because the dialog still reads as open.
 *
 * Only dialogs that were modal going in are restored, so a deliberately non-modal one is left
 * alone, and the `open` attribute is dropped rather than the dialog `close()`d: `close()` fires an
 * event other components read as the shopper closing it.
 *
 * @param {HTMLDialogElement[]} dialogs - the dialogs that were modal before the morph
 */
function restoreModality(dialogs) {
  for (const dialog of dialogs) {
    if (!dialog.isConnected || dialog.matches(':modal')) continue;

    dialog.removeAttribute('open');
    dialog.showModal();
  }
}

export const sectionRenderer = new SectionRenderer();
